<template>
  <div class="simple-editor-wrapper">
    <!-- Toolbar -->
    <div class="editor-toolbar mb-2">
      <!-- Texto base -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Negrita (Ctrl+B)"
          @click="applyFormat('bold')"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Cursiva (Ctrl+I)"
          @click="applyFormat('italic')"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Subrayado (Ctrl+U)"
          @click="applyFormat('underline')"
        >
          <u>U</u>
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Tachado"
          @click="applyFormat('strikethrough')"
        >
          <s>S</s>
        </button>
      </div>

      <div class="toolbar-divider" />

      <!-- Estilos de párrafo -->
      <div class="toolbar-group">
        <select
          class="editor-select"
          @change="changeParagraphStyle"
          :value="currentParagraphStyle"
        >
          <option value="">
            Párrafo normal
          </option>
          <option value="h1">
            Encabezado 1
          </option>
          <option value="h2">
            Encabezado 2
          </option>
          <option value="h3">
            Encabezado 3
          </option>
          <option value="h4">
            Encabezado 4
          </option>
          <option value="h5">
            Encabezado 5
          </option>
          <option value="h6">
            Encabezado 6
          </option>
          <option value="pre">
            Código
          </option>
          <option value="blockquote">
            Comilla
          </option>
        </select>
      </div>

      <div class="toolbar-divider" />

      <!-- Listas -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Lista con viñetas"
          @click="formatList('ul')"
        >
          • Lista
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Lista numerada"
          @click="formatList('ol')"
        >
          1. Lista
        </button>
      </div>

      <div class="toolbar-divider" />

      <!-- Alineación -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Alinear a la izquierda"
          @click="applyFormat('justifyLeft')"
        >
          ⬅
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Alinear al centro"
          @click="applyFormat('justifyCenter')"
        >
          ↔
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Alinear a la derecha"
          @click="applyFormat('justifyRight')"
        >
          ➡
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Justificar"
          @click="applyFormat('justifyFull')"
        >
          ◆
        </button>
      </div>

      <div class="toolbar-divider" />

      <!-- Enlaces e imágenes -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Insertar enlace"
          @click="insertLink"
        >
          🔗
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Insertar imagen"
          @click="insertImage"
        >
          🖼️
        </button>
      </div>

      <div class="toolbar-divider" />

      <!-- Línea horizontal -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Línea horizontal"
          @click="insertHorizontalRule"
        >
          ──
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Tabla"
          @click="insertTable"
        >
          ⊞
        </button>
      </div>

      <div class="toolbar-divider" />

      <!-- Utilidades -->
      <div class="toolbar-group">
        <button
          type="button"
          class="editor-btn"
          title="Insertar HTML personalizado"
          @click="showHtmlModal = true"
        >
          &lt;/&gt;
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Ver/Editar HTML"
          @click="showCode = !showCode"
        >
          { }
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Deshacer (Ctrl+Z)"
          @click="applyFormat('undo')"
        >
          ↶
        </button>
        <button
          type="button"
          class="editor-btn"
          title="Rehacer (Ctrl+Y)"
          @click="applyFormat('redo')"
        >
          ↷
        </button>
        <button
          type="button"
          class="editor-btn btn-clear"
          title="Limpiar formato"
          @click="clearFormatting"
        >
          ✕
        </button>
      </div>
    </div>

    <!-- Editor visual o código -->
    <div v-if="!showCode" class="editor-content">
      <div
        ref="editor"
        class="editor-area"
        contenteditable="true"
        @input="updateModel"
        @paste="handlePaste"
      />
    </div>
    <div v-else class="code-editor">
      <textarea
        :value="modelValue"
        class="code-textarea"
        @input="updateFromCode"
      />
    </div>

    <!-- Modal para insertar HTML -->
    <div
      v-if="showHtmlModal"
      class="modal-overlay"
      @click.self="showHtmlModal = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h5>Insertar HTML personalizado</h5>
          <button
            type="button"
            class="close-btn"
            @click="showHtmlModal = false"
          >
            &times;
          </button>
        </div>
        <div class="modal-body">
          <textarea
            v-model="htmlInput"
            class="html-textarea"
            placeholder="Pega tu HTML aquí..."
          />
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn-insert"
            @click="insertHtml"
          >
            Insertar
          </button>
          <button
            type="button"
            class="btn-cancel"
            @click="showHtmlModal = false"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para insertar enlace -->
    <div
      v-if="showLinkModal"
      class="modal-overlay"
      @click.self="showLinkModal = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h5>Insertar enlace</h5>
          <button
            type="button"
            class="close-btn"
            @click="showLinkModal = false"
          >
            &times;
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>URL:</label>
            <input
              v-model="linkUrl"
              type="url"
              class="form-input"
              placeholder="https://ejemplo.com"
            />
          </div>
          <div class="form-group">
            <label>Texto del enlace:</label>
            <input
              v-model="linkText"
              type="text"
              class="form-input"
              placeholder="Haz clic aquí"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn-insert"
            @click="confirmLink"
          >
            Insertar
          </button>
          <button
            type="button"
            class="btn-cancel"
            @click="showLinkModal = false"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para insertar imagen -->
    <div
      v-if="showImageModal"
      class="modal-overlay"
      @click.self="showImageModal = false"
    >
      <div class="modal-box">
        <div class="modal-header">
          <h5>Insertar imagen</h5>
          <button
            type="button"
            class="close-btn"
            @click="showImageModal = false"
          >
            &times;
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>URL de la imagen:</label>
            <input
              v-model="imageUrl"
              type="url"
              class="form-input"
              placeholder="https://ejemplo.com/imagen.jpg"
            />
          </div>
          <div class="form-group">
            <label>Ancho (px):</label>
            <input
              v-model.number="imageWidth"
              type="number"
              class="form-input"
              placeholder="300"
            />
          </div>
          <div class="form-group">
            <label>Alto (px):</label>
            <input
              v-model.number="imageHeight"
              type="number"
              class="form-input"
              placeholder="200"
            />
          </div>
          <div class="form-group">
            <label>Texto alternativo:</label>
            <input
              v-model="imageAlt"
              type="text"
              class="form-input"
              placeholder="Descripción de la imagen"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn-insert"
            @click="confirmImage"
          >
            Insertar
          </button>
          <button
            type="button"
            class="btn-cancel"
            @click="showImageModal = false"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SimpleHtmlEditor',
  props: {
    value: {
      type: String,
      default: '',
    },
  },
  emits: ['input'],
  data() {
    return {
      showHtmlModal: false,
      showLinkModal: false,
      showImageModal: false,
      showCode: false,
      htmlInput: '',
      linkUrl: '',
      linkText: '',
      imageUrl: '',
      imageWidth: 300,
      imageHeight: 200,
      imageAlt: '',
      currentParagraphStyle: '',
      styleTags: [], // Almacenar los estilos CSS que se insertan
    }
  },
  watch: {
    value(newValue) {
      // Solo actualizar si el ref existe y el contenido es diferente
      if (this.$refs.editor && newValue !== this.$refs.editor.innerHTML && !this.showCode) {
        this.$refs.editor.innerHTML = newValue
      }
    },
  },
  mounted() {
    // Inicializar el contenido del editor en el siguiente tick
    this.$nextTick(() => {
      if (this.$refs.editor) {
        if (this.value) {
          this.$refs.editor.innerHTML = this.value
        } else {
          this.$refs.editor.innerHTML = ''
        }
        // Asegurar que el editor está listo para edición
        this.$refs.editor.focus()
        this.$refs.editor.blur()
      }
    })
  },
  methods: {
    getCompleteHtml() {
      // Obtener los estilos que se agregaron durante la edición
      let styleContent = ''
      this.styleTags.forEach(style => {
        styleContent += `<style>${style}</style>`
      })

      // Combinar estilos + contenido del editor
      const editorContent = this.$refs.editor.innerHTML
      return styleContent + editorContent
    },
    updateModel() {
      this.$emit('input', this.getCompleteHtml())
    },
    updateFromCode(event) {
      const htmlContent = event.target.value
      // Extraer estilos del HTML pegado
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      const styleElements = tempDiv.querySelectorAll('style')

      // Limpiar styleTags anteriores y agregar los nuevos
      this.styleTags = []
      styleElements.forEach(style => {
        this.styleTags.push(style.textContent)
      })

      this.$emit('input', this.getCompleteHtml())
      // Actualizar el editor visual con el HTML
      if (this.$refs.editor) {
        this.$refs.editor.innerHTML = htmlContent
      }
    },
    applyFormat(command) {
      document.execCommand(command, false, null)
      this.$refs.editor.focus()
      this.updateModel()
    },
    handlePaste(e) {
      e.preventDefault()
      const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain')
      if (text) {
        // Crear un contenedor temporal para parsear el HTML
        const temp = document.createElement('div')
        temp.innerHTML = text

        // Extraer y almacenar estilos CSS
        const styles = temp.querySelectorAll('style')
        styles.forEach(style => {
          this.styleTags.push(style.textContent)
          // Agregar los estilos al documento
          const newStyle = document.createElement('style')
          newStyle.textContent = style.textContent
          document.head.appendChild(newStyle)
        })

        // Usar Selection API para mejor manejo de estilos
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          range.deleteContents()

          // Crear contenedor nuevo sin los style tags
          const tempContent = document.createElement('div')
          tempContent.innerHTML = text

          // Insertar cada nodo (excepto style tags)
          while (tempContent.firstChild) {
            if (tempContent.firstChild.nodeName !== 'STYLE') {
              range.insertNode(tempContent.firstChild)
            } else {
              tempContent.removeChild(tempContent.firstChild)
            }
          }

          // Mover cursor al final del contenido pegado
          range.collapse(false)
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // Fallback
          document.execCommand('insertHTML', false, text)
        }
        this.updateModel()
      }
    },
    changeParagraphStyle(event) {
      const style = event.target.value
      this.currentParagraphStyle = style

      if (style === '') {
        document.execCommand('formatBlock', false, '<p>')
      } else if (style === 'pre') {
        document.execCommand('formatBlock', false, '<pre>')
      } else if (style === 'blockquote') {
        document.execCommand('formatBlock', false, '<blockquote>')
      } else {
        document.execCommand('formatBlock', false, `<${style}>`)
      }

      this.$refs.editor.focus()
      this.updateModel()
    },
    formatList(type) {
      if (type === 'ul') {
        document.execCommand('insertUnorderedList', false, null)
      } else if (type === 'ol') {
        document.execCommand('insertOrderedList', false, null)
      }
      this.$refs.editor.focus()
      this.updateModel()
    },
    insertHorizontalRule() {
      document.execCommand('insertHorizontalRule', false, null)
      this.$refs.editor.focus()
      this.updateModel()
    },
    insertTable() {
      const html = '<table border="1" cellpadding="5" cellspacing="0" style="width:100%;"><tr><td>Celda 1</td><td>Celda 2</td><td>Celda 3</td></tr><tr><td>Celda 4</td><td>Celda 5</td><td>Celda 6</td></tr></table>'
      document.execCommand('insertHTML', false, html)
      this.$refs.editor.focus()
      this.updateModel()
    },
    insertLink() {
      this.linkUrl = ''
      this.linkText = ''
      this.showLinkModal = true
    },
    confirmLink() {
      if (this.linkUrl.trim()) {
        const text = this.linkText.trim() || this.linkUrl
        const html = `<a href="${this.linkUrl}" target="_blank">${text}</a>`
        this.$refs.editor.focus()
        document.execCommand('insertHTML', false, html)
        this.$nextTick(() => {
          this.$emit('input', this.getCompleteHtml())
          this.showLinkModal = false
          this.linkUrl = ''
          this.linkText = ''
        })
      }
    },
    insertImage() {
      this.imageUrl = ''
      this.imageWidth = 300
      this.imageHeight = 200
      this.imageAlt = ''
      this.showImageModal = true
    },
    confirmImage() {
      if (this.imageUrl.trim()) {
        const style = `width:${this.imageWidth}px;height:${this.imageHeight}px;`
        const html = `<img src="${this.imageUrl}" alt="${this.imageAlt}" style="${style}" />`
        this.$refs.editor.focus()
        document.execCommand('insertHTML', false, html)
        this.$nextTick(() => {
          this.$emit('input', this.getCompleteHtml())
          this.showImageModal = false
          this.imageUrl = ''
          this.imageWidth = 300
          this.imageHeight = 200
          this.imageAlt = ''
        })
      }
    },
    insertHtml() {
      if (this.htmlInput.trim()) {
        this.$refs.editor.focus()

        // Crear un contenedor temporal para parsear el HTML
        const temp = document.createElement('div')
        temp.innerHTML = this.htmlInput

        // Extraer y almacenar estilos CSS
        const styles = temp.querySelectorAll('style')
        styles.forEach(style => {
          this.styleTags.push(style.textContent)
          // Agregar los estilos al documento para visualización inmediata
          const newStyle = document.createElement('style')
          newStyle.textContent = style.textContent
          document.head.appendChild(newStyle)
        })

        // Usar Selection API para mejor manejo de estilos
        const selection = window.getSelection()
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          range.deleteContents()

          // Insertar cada nodo del contenedor temporal (excepto style tags)
          while (temp.firstChild) {
            if (temp.firstChild.nodeName !== 'STYLE') {
              range.insertNode(temp.firstChild)
            } else {
              temp.removeChild(temp.firstChild)
            }
          }

          // Mover cursor al final del contenido insertado
          range.collapse(false)
          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          // Fallback si no hay selección
          document.execCommand('insertHTML', false, this.htmlInput)
        }

        this.$nextTick(() => {
          this.$emit('input', this.getCompleteHtml())
          this.showHtmlModal = false
          this.htmlInput = ''
        })
      }
    },
    clearFormatting() {
      document.execCommand('removeFormat', false, null)
      this.$refs.editor.focus()
      this.updateModel()
    },
  },
}
</script>

<style scoped lang="scss">
.simple-editor-wrapper {
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  background-color: #fff;
}

.editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 0.25rem 0.25rem 0 0;

  .toolbar-group {
    display: flex;
    gap: 0.25rem;
  }

  .toolbar-divider {
    width: 1px;
    height: 24px;
    background-color: #ddd;
    margin: 0 0.25rem;
  }
}

.editor-btn {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  background-color: #fff;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  min-width: 32px;
  white-space: nowrap;

  &:hover {
    background-color: #f0f0f0;
    border-color: #bbb;
  }

  &:active {
    transform: scale(0.95);
  }

  &.btn-clear {
    color: #dc3545;

    &:hover {
      background-color: #f8d7da;
    }
  }
}

.editor-select {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ddd;
  background-color: #fff;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    border-color: #bbb;
  }

  &:focus {
    outline: none;
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
}

.editor-content {
  padding: 1rem;
  min-height: 300px;
  max-height: 500px;
  overflow-y: auto;
}

.editor-area {
  outline: none;
  font-size: 0.95rem;
  line-height: 1.5;
  word-wrap: break-word;
  padding: 0.5rem 0;

  &:focus {
    outline: none;
  }

  // Reset de estilos por defecto para permitir que los estilos inline funcionen
  * {
    box-sizing: border-box;
  }

  // Headings
  h1, h2, h3, h4, h5, h6 {
    margin: 1rem 0 0.5rem 0;
    font-weight: 600;
  }

  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }
  h5 { font-size: 1.1rem; }
  h6 { font-size: 1rem; }

  // Lists
  ul, ol {
    margin: 0.5rem 0;
    padding-left: 2rem;

    // Nested lists
    ul, ol {
      margin: 0;
    }
  }

  li {
    margin: 0.25rem 0;
  }

  // Paragraphs
  p {
    margin: 0.5rem 0;
  }

  // Divs y contenedores - Sin bordes por defecto
  div {
    // Los divs heredan los estilos inline del HTML pegado
  }

  // Horizontal Rule
  hr {
    margin: 1rem 0;
    border: none;
    border-top: 1px solid #ddd;
  }

  // Blockquotes
  blockquote {
    border-left: 4px solid #ddd;
    margin-left: 0;
    padding-left: 1rem;
    color: #666;
    background-color: #f9f9f9;
  }

  // Code blocks
  pre {
    background-color: #f5f5f5;
    padding: 1rem;
    border-radius: 0.25rem;
    overflow-x: auto;
    border: 1px solid #ddd;

    code {
      color: #333;
    }
  }

  // Inline code
  code {
    background-color: #f5f5f5;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: monospace;
    font-size: 0.9em;
  }

  // Tables
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;

    td, th {
      border: 1px solid #ddd;
      padding: 0.5rem;
    }

    th {
      background-color: #f5f5f5;
      font-weight: 600;
    }
  }

  // Images
  img {
    max-width: 100%;
    height: auto;
    margin: 0.5rem 0;
    display: block;
  }

  // Links
  a {
    color: #007bff;
    text-decoration: underline;
  }

  // Text formatting
  strong, b {
    font-weight: bold;
  }

  em, i {
    font-style: italic;
  }

  u {
    text-decoration: underline;
  }

  s, strike {
    text-decoration: line-through;
  }

  small {
    font-size: 0.85em;
  }

  sub {
    vertical-align: sub;
    font-size: 0.8em;
  }

  sup {
    vertical-align: super;
    font-size: 0.8em;
  }

  mark {
    background-color: yellow;
    padding: 0.1rem 0.2rem;
  }

  // Article - permitir que herede estilos
  article {
    // Sin estilos por defecto
  }

  // Section - Sin estilos por defecto para permitir los inline
  section {
    // Sin estilos por defecto
  }

  // Header - Sin estilos por defecto
  header {
    // Sin estilos por defecto
  }

  // Spans - Sin estilos por defecto
  span {
    // Los spans heredan los estilos inline
  }
}

.code-editor {
  padding: 1rem;
  background-color: #f8f9fa;
}

.code-textarea {
  width: 100%;
  min-height: 300px;
  max-height: 500px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
}

// Modal
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-box {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;

  h5 {
    margin: 0;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;

    &:hover {
      color: #000;
    }
  }
}

.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #333;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
}

.html-textarea {
  width: 100%;
  height: 250px;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1.5rem;
  border-top: 1px solid #e0e0e0;
  background-color: #f8f9fa;
}

.btn-insert,
.btn-cancel {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
  }

  &:active {
    transform: scale(0.95);
  }
}

.btn-insert {
  background-color: #dc3545;
  color: white;
  border-color: #c82333;

  &:hover {
    background-color: #c82333;
    border-color: #bd2130;
  }
}

.btn-cancel {
  background-color: #fff;
  color: #333;

  &:hover {
    background-color: #e9ecef;
  }
}
</style>

